import React, { useEffect, useState } from 'react';
import { Card, Container, Row } from 'react-bootstrap';
import { CardBody, Modal, ModalBody, ModalHeader } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { Copy, Trash2 } from 'react-feather';
import { Btn } from '../../AbstractElements';
import {
  useGetReviewsQuery,
  useLazyExportReviewsQuery,
  useUpdateReviewStatusMutation,
  useDeleteReviewMutation,
} from '../../features/reviewsApiSlice';
import { toast } from 'react-toastify';
import ConfirmationModal from '../UiKits/ConfirmationModal';

const STATUS_OPTIONS = ['pending', 'published', 'rejected'];
const ROW_EVENT_TAG = 'allowRowEvents';

const Stars = ({ rating }) => (
  <span data-tag={ROW_EVENT_TAG} title={`${rating} / 5`} aria-label={`${rating} out of 5 stars`}>
    {'★'.repeat(rating)}
    <span data-tag={ROW_EVENT_TAG} style={{ opacity: 0.25 }}>
      {'★'.repeat(5 - rating)}
    </span>
  </span>
);

const formatDate = (value) => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value));
};

const ReviewPreview = ({ value }) => (
  <span data-tag={ROW_EVENT_TAG} className="t-line-clamp-2 t-max-w-[180px] t-leading-[1.4]" title={value || ''}>
    {value || '—'}
  </span>
);

const DetailAnswer = ({ label, value }) => (
  <div className="mb-4">
    <h6 className="mb-1">{label}</h6>
    <div className="t-whitespace-pre-wrap t-break-words text-muted">{value || 'No answer provided.'}</div>
  </div>
);

const Reviews = () => {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [selectedReview, setSelectedReview] = useState(null);
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: reviews,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    refetch,
  } = useGetReviewsQuery({
    page: currentPage,
    limit: pageSize,
    search: debouncedSearchTerm,
    status: statusFilter,
    rating: ratingFilter,
  });

  const [exportReviews] = useLazyExportReviewsQuery();
  const [updateReviewStatus] = useUpdateReviewStatusMutation();
  const [deleteReview] = useDeleteReviewMutation();

  useEffect(() => {
    if (!isSuccess || !reviews) return;
    setData(Array.isArray(reviews.data) ? reviews.data : []);
    setTotalRows(reviews.pagination?.total || 0);
  }, [reviews, isSuccess]);

  const handleSearch = (event) => setSearchTerm(event.target.value);

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleRatingFilterChange = (event) => {
    setRatingFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateReviewStatus({ id, status }).unwrap();
      toast.success('Review status updated');
      refetch();
    } catch (requestError) {
      toast.error(requestError?.data?.message || 'Failed to update review status');
    }
  };

  const handleCopyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success('Email copied');
    } catch {
      toast.error('Could not copy email');
    }
  };

  const handleDelete = async () => {
    const review = reviewToDelete;
    if (!review) return;

    try {
      await deleteReview(review.id).unwrap();
      toast.success('Review deleted');
      setSelectedReview((current) => (current?.id === review.id ? null : current));

      if (data.length === 1 && currentPage > 1) {
        setCurrentPage((page) => page - 1);
      } else {
        refetch();
      }
    } catch (requestError) {
      toast.error(requestError?.data?.message || 'Failed to delete review');
    }
  };

  const downloadReviewsCSV = async () => {
    try {
      setIsDownloadingCsv(true);
      const csvBlob = await exportReviews({
        search: debouncedSearchTerm,
        status: statusFilter,
        rating: ratingFilter,
      }).unwrap();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(csvBlob);
      link.download = `reviews-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success('CSV downloaded');
    } catch (requestError) {
      toast.error(requestError?.data?.message || 'Could not download CSV');
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  const tableColumns = [
    {
      name: 'ID',
      selector: (row) => `${row?.id || ''}`,
      width: '64px',
    },
    {
      name: 'Email',
      selector: (row) => `${row?.email || ''}`,
      width: '210px',
      cell: (row) => (
        <div className="t-flex t-items-center t-gap-1" onClick={(event) => event.stopPropagation()}>
          <span className="t-inline-block t-max-w-[145px] t-truncate t-align-middle" title={row?.email || ''}>
            {row?.email || '—'}
          </span>
          <button
            type="button"
            className="t-inline-flex t-h-8 t-w-8 t-items-center t-justify-center t-border-0 t-bg-transparent t-p-0 t-text-inherit hover:t-text-secondary focus-visible:t-outline focus-visible:t-outline-2 focus-visible:t-outline-offset-2 focus-visible:t-outline-secondary"
            title="Copy email"
            aria-label={`Copy ${row?.email || 'email'}`}
            onClick={() => handleCopyEmail(row?.email || '')}
          >
            <Copy size={15} />
          </button>
        </div>
      ),
      ignoreRowClick: true,
    },
    {
      name: 'Rating',
      selector: (row) => row?.rating || 0,
      width: '110px',
      cell: (row) => <Stars rating={row?.rating || 0} />,
    },
    {
      name: 'Problems',
      selector: (row) => `${row?.problems_text || ''}`,
      width: '190px',
      cell: (row) => <ReviewPreview value={row?.problems_text} />,
    },
    {
      name: 'Why riise',
      selector: (row) => `${row?.purchase_reason_text || ''}`,
      width: '190px',
      cell: (row) => <ReviewPreview value={row?.purchase_reason_text} />,
    },
    {
      name: 'Improve',
      selector: (row) => `${row?.improve_text || ''}`,
      width: '190px',
      cell: (row) => <ReviewPreview value={row?.improve_text} />,
    },
    {
      name: 'Contact',
      selector: (row) => (row?.contact_ok ? 'Yes' : 'No'),
      width: '96px',
    },
    {
      name: 'Source',
      selector: (row) => (row?.verified ? 'Verified link' : 'Manual email'),
      width: '130px',
      cell: (row) =>
        row?.verified ? (
          <span
            data-tag={ROW_EVENT_TAG}
            className="t-inline-flex t-items-center t-rounded-full t-bg-[#dff5e7] t-px-2.5 t-py-1 t-text-xs t-font-semibold t-leading-none t-text-[#18794e]"
          >
            Verified link
          </span>
        ) : (
          <span
            data-tag={ROW_EVENT_TAG}
            className="t-inline-flex t-items-center t-rounded-full t-bg-gray-100 t-px-2.5 t-py-1 t-text-xs t-font-semibold t-leading-none t-text-gray-600"
            title="Email address was entered manually"
          >
            Manual email
          </span>
        ),
    },
    {
      name: 'Status',
      selector: (row) => `${row?.status || ''}`,
      width: '150px',
      cell: (row) => (
        <select
          className="form-select form-select-sm"
          value={row?.status || 'pending'}
          aria-label={`Status for review ${row.id}`}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => handleStatusChange(row.id, event.target.value)}
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ),
      ignoreRowClick: true,
    },
    {
      name: 'Created',
      selector: (row) => row?.created_at || '',
      width: '165px',
      cell: (row) => formatDate(row?.created_at),
    },
    {
      name: 'Actions',
      width: '56px',
      conditionalCellStyles: [
        {
          when: () => true,
          // react-data-table-component gives every cell `position: relative`
          // from its runtime stylesheet, after Tailwind has loaded. Its column
          // style API is therefore required here so sticky positioning wins.
          style: {
            position: 'sticky',
            right: 0,
            zIndex: 2,
            backgroundColor: '#fff',
            boxShadow: '-8px 0 12px -12px rgba(0, 0, 0, 0.45)',
          },
        },
      ],
      cell: (row) => (
        <button
          type="button"
          className="btn btn-sm btn-outline-danger t-inline-flex t-h-8 t-w-8 t-items-center t-justify-center t-p-0"
          title="Delete review"
          aria-label={`Delete review from ${row?.email || 'this address'}`}
          onClick={(event) => {
            event.stopPropagation();
            setReviewToDelete(row);
          }}
        >
          <Trash2 size={16} />
        </button>
      ),
      ignoreRowClick: true,
    },
  ];

  const hasFilters = Boolean(searchTerm || statusFilter || ratingFilter);

  return (
    <>
      <h1 className="t-text-xl t-font-bold t-mb-2 t-ml-2 md:t-ml-3 t-text-secondary">Reviews</h1>
      <Container fluid>
        <Card>
          <CardBody>
            <Row className="g-3 mb-3 align-items-end">
              <div className="col-12 col-lg-5">
                <input
                  type="text"
                  placeholder="Search reviews..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="form-control"
                />
              </div>
              <div className="col-6 col-lg-2">
                <select className="form-select" value={statusFilter} onChange={handleStatusFilterChange}>
                  <option value="">All statuses</option>
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6 col-lg-2">
                <select className="form-select" value={ratingFilter} onChange={handleRatingFilterChange}>
                  <option value="">All ratings</option>
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <option key={rating} value={rating}>
                      {rating} star{rating === 1 ? '' : 's'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-lg-3 text-lg-end">
                <Btn
                  attrBtn={{
                    color: 'primary',
                    disabled: isDownloadingCsv || isFetching,
                    onClick: downloadReviewsCSV,
                  }}
                >
                  {isDownloadingCsv ? 'Downloading...' : 'Download CSV'}
                </Btn>
              </div>
            </Row>

            {isError ? (
              <div className="text-center py-5">
                <p className="mb-3 text-danger">{error?.data?.message || 'Could not load reviews.'}</p>
                <button type="button" className="btn btn-outline-primary" onClick={refetch}>
                  Try again
                </button>
              </div>
            ) : (
              <>
                <div className="mb-3 text-muted mt-3">
                  Showing {data.length} of {totalRows} reviews
                  {hasFilters && ' (filtered)'}
                </div>
                <DataTable
                  columns={tableColumns}
                  data={data}
                  striped
                  highlightOnHover
                  pointerOnHover
                  onRowClicked={setSelectedReview}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  onChangePage={setCurrentPage}
                  onChangeRowsPerPage={(newPerPage, page) => {
                    setPageSize(newPerPage);
                    setCurrentPage(page);
                  }}
                  progressPending={isLoading || isFetching}
                  progressComponent={<div className="py-4 text-muted">Loading reviews...</div>}
                  noDataComponent={<div className="py-5 text-muted">No reviews match these filters.</div>}
                  paginationPerPage={pageSize}
                  paginationRowsPerPageOptions={[10, 25, 50, 100]}
                />
              </>
            )}
          </CardBody>
        </Card>
      </Container>

      <Modal isOpen={Boolean(selectedReview)} toggle={() => setSelectedReview(null)} size="lg">
        <ModalHeader toggle={() => setSelectedReview(null)}>Review #{selectedReview?.id}</ModalHeader>
        <ModalBody>
          <div className="row mb-4">
            <div className="col-md-6 mb-3 mb-md-0">
              <div className="text-muted small">Email</div>
              <div>{selectedReview?.email || '—'}</div>
            </div>
            <div className="col-md-2 mb-3 mb-md-0">
              <div className="text-muted small">Rating</div>
              <Stars rating={selectedReview?.rating || 0} />
            </div>
            <div className="col-md-2 mb-3 mb-md-0">
              <div className="text-muted small">Contact</div>
              <div>{selectedReview?.contact_ok ? 'Yes' : 'No'}</div>
            </div>
            <div className="col-md-2">
              <div className="text-muted small">Source</div>
              <div>{selectedReview?.verified ? 'Verified link' : 'Manual email'}</div>
            </div>
          </div>
          <DetailAnswer label="Problems" value={selectedReview?.problems_text} />
          <DetailAnswer label="Why riise" value={selectedReview?.purchase_reason_text} />
          <DetailAnswer label="What we can improve" value={selectedReview?.improve_text} />
          <div className="text-muted small">Submitted {formatDate(selectedReview?.created_at)}</div>
        </ModalBody>
      </Modal>

      <ConfirmationModal
        message={`Delete the review from ${reviewToDelete?.email || 'this address'}? This frees the address to be reviewed again.`}
        isOpen={Boolean(reviewToDelete)}
        toggle={() => setReviewToDelete(null)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default Reviews;
